export type Guid = string;

export class GuidParser {

  static newGuid(): Guid {
    const guid: Guid = crypto.randomUUID();
    return guid;
  }

  static containsNumber(value: string | number | bigint): boolean {
    const isNumber = (typeof value === 'number' || typeof value === 'bigint') || this.numberRegex.test(value);
    return isNumber;
  }

  static containsGuid(value: string | number | bigint): boolean {
    const isGuid = typeof value === 'string' && this.guidRegex.test(value);
    return isGuid;
  }

  static fromStringOrNumber(value: string | number | bigint): Guid {
    const guid: Guid = this.containsNumber(value)
      ? this.fromNumber(BigInt(value))
      : this.fromString(String(value));
    return guid;
  }

  static fromString(value: string): Guid {
    const guid: Guid = this.containsGuid(value)
      ? value.toLowerCase()
      : null;
    return guid;
  }

  // This method is equivalent to C# method: (long value) => new Guid([.. BitConverter.GetBytes(value), .. new byte[8]]);
  static fromNumber(value: bigint): Guid {

    const arrayBuffer = new ArrayBuffer(16);
    const dataView = new DataView(arrayBuffer);

    // Write the bigint into the first 8 bytes (little-endian).
    const byteOffset = 0;
    const littleEndian = true;
    dataView.setBigUint64(byteOffset, value, littleEndian);

    // Remaining 8 bytes are zero-initialized by default.

    const uint8Array = new Uint8Array(arrayBuffer);

    // .NET Guid constructor reorders the first 8 bytes:
    // Data1 (4 bytes): reverse bytes 0–3
    // Data2 (2 bytes): reverse bytes 4–5
    // Data3 (2 bytes): reverse bytes 6–7
    // Data4 (8 bytes): bytes 8–15 unchanged
    const bytes = [
      ...uint8Array.slice(0, 4).reverse(), // Data1
      ...uint8Array.slice(4, 6).reverse(), // Data2
      ...uint8Array.slice(6, 8).reverse(), // Data3
      ...uint8Array.slice(8, 16)           // Data4
    ];

    const guid: Guid = this.fromByteArray(bytes);
    return guid;
  }

  static fromByteArray(value: number[]): Guid {

    // Convert to a hexadecimal string: xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx
    const radix = 16;
    const uint8ToHexString = (uint8: number): string => uint8.toString(radix).padStart(2, '0');
    const hex: string = value
      .map(uint8 => uint8ToHexString(uint8))
      .join('');

    // Format as a GUID: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
    const guid: Guid = `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    return guid;
  }

  private static readonly numberRegex = /^-?\d+$/;
  private static readonly guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
}
